/**
 * Truth or Dare - Turn-based Game Renderer
 */
import { t } from '../utils/i18n.js';
import { Utils } from '../utils/ui.js';
import { AppState } from '../config.js';

const lang = () => document.documentElement.lang === 'he' ? 'he' : 'en';

export function renderInvite(message, isOwn, partnerName) {
    const gd = message.game_data;
    const gameName = gd.gameName?.[lang()] || gd.gameName?.en || 'Truth or Dare';
    const emoji = gd.gameEmoji || '🎯';
    const sessionId = gd.sessionId;
    const status = gd.status;

    let actionHtml = '';
    if (status === 'expired') {
        actionHtml = `<span class="text-gray-400 text-sm">${t('games.expired')}</span>`;
    } else if (status === 'declined') {
        actionHtml = `<span class="text-gray-400 text-sm">${t('games.declined')}</span>`;
    } else if (isOwn) {
        actionHtml = `<span class="text-fuchsia-400 text-sm game-waiting-dots">${t('games.inviteSent', { name: partnerName })}</span>`;
    } else {
        actionHtml = `
            <div class="flex gap-2 mt-3">
                <button class="game-accept-btn flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium transition-all" data-session-id="${sessionId}">
                    ${t('games.accept')}
                </button>
                <button class="game-decline-btn flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium transition-all" data-session-id="${sessionId}">
                    ${t('games.decline')}
                </button>
            </div>
        `;
    }

    return `
        <div class="game-card game-invite-card" data-session-id="${sessionId}" style="animation: gameCardAppear 0.4s ease-out">
            <div class="flex items-center gap-2 mb-2">
                <span class="text-2xl">${emoji}</span>
                <span class="font-semibold text-fuchsia-600 dark:text-fuchsia-300">${Utils.escapeHtml(gameName)}</span>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-1">
                ${isOwn
                    ? t('games.inviteSent', { name: Utils.escapeHtml(partnerName) })
                    : t('games.wantsToPlay', { name: Utils.escapeHtml(partnerName), game: Utils.escapeHtml(gameName) })}
            </p>
            ${actionHtml}
        </div>
    `;
}

export function renderGameCard(sessionId, state, currentUserId, partnerName) {
    const round = state.current_round || 1;
    const total = state.total_rounds || 6;
    const gameState = state.state || {};
    const roundData = gameState.rounds?.[round];
    const isChooser = String(gameState.currentChooser) === String(currentUserId);
    const isTarget = String(gameState.currentTarget) === String(currentUserId);

    const progress = Math.round((round / total) * 100);
    let bodyHtml = '';

    if (!roundData || !roundData.chosenType) {
        // Chooser picks truth or dare
        if (isChooser) {
            bodyHtml = `
                <p class="text-center text-sm text-gray-600 mb-3">${t('games.chooseFor', { name: Utils.escapeHtml(partnerName) })}</p>
                <div class="grid grid-cols-2 gap-3">
                    <button class="game-option game-tod-choose-btn p-4 rounded-xl border-2 border-fuchsia-200 hover:border-fuchsia-400 bg-fuchsia-50 transition-all text-center" data-session-id="${sessionId}" data-choice="truth">
                        <span class="text-3xl block mb-2">🤫</span>
                        <span class="text-sm font-medium">${t('games.truth')}</span>
                    </button>
                    <button class="game-option game-tod-choose-btn p-4 rounded-xl border-2 border-red-200 hover:border-red-400 bg-red-50 transition-all text-center" data-session-id="${sessionId}" data-choice="dare">
                        <span class="text-3xl block mb-2">🔥</span>
                        <span class="text-sm font-medium">${t('games.dare')}</span>
                    </button>
                </div>
            `;
        } else {
            bodyHtml = `
                <div class="text-center py-4">
                    <p class="text-fuchsia-500 font-medium game-waiting-dots">${t('games.waitingForAnswer', { name: Utils.escapeHtml(partnerName) })}</p>
                </div>
            `;
        }
    } else if (!roundData.completed) {
        // Prompt shown, target needs to complete
        const l = lang();
        const prompt = roundData.prompt?.[l] || roundData.prompt?.en || '';
        const typeEmoji = roundData.chosenType === 'truth' ? '🤫' : '🔥';
        const typeLabel = roundData.chosenType === 'truth' ? t('games.truth') : t('games.dare');

        if (isTarget) {
            bodyHtml = `
                <div class="text-center">
                    <div class="inline-flex items-center gap-2 bg-fuchsia-100 dark:bg-fuchsia-900/30 rounded-full px-3 py-1 mb-3">
                        <span>${typeEmoji}</span>
                        <span class="text-sm font-semibold text-fuchsia-600 dark:text-fuchsia-300">${typeLabel}</span>
                    </div>
                    <p class="text-base font-medium text-gray-800 dark:text-gray-200 mb-4">${Utils.escapeHtml(prompt)}</p>
                    <button class="game-tod-done-btn bg-green-500 hover:bg-green-600 text-white py-2 px-6 rounded-lg font-medium transition-all" data-session-id="${sessionId}">
                        ${t('games.done')}
                    </button>
                </div>
            `;
        } else {
            bodyHtml = `
                <div class="text-center">
                    <div class="inline-flex items-center gap-2 bg-fuchsia-100 dark:bg-fuchsia-900/30 rounded-full px-3 py-1 mb-3">
                        <span>${typeEmoji}</span>
                        <span class="text-sm font-semibold text-fuchsia-600 dark:text-fuchsia-300">${typeLabel}</span>
                    </div>
                    <p class="text-base font-medium text-gray-800 dark:text-gray-200 mb-4">${Utils.escapeHtml(prompt)}</p>
                    <p class="text-fuchsia-500 font-medium game-waiting-dots">${t('games.waitingForAnswer', { name: Utils.escapeHtml(partnerName) })}</p>
                </div>
            `;
        }
    } else {
        // Round completed, waiting for next
        bodyHtml = `
            <div class="text-center py-4">
                <p class="text-green-500 font-medium">✓ ${t('games.done')}</p>
            </div>
        `;
    }

    return `
        <div class="game-card game-active-card" data-session-id="${sessionId}" style="animation: gameCardAppear 0.4s ease-out">
            <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2">
                    <span class="text-xl">🎯</span>
                    <span class="font-semibold text-sm text-fuchsia-600 dark:text-fuchsia-300">${t('games.truthOrDare')}</span>
                </div>
                <span class="text-xs text-gray-500">${t('games.round', { current: round, total: total })}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-1.5 mb-4">
                <div class="bg-fuchsia-500 h-1.5 rounded-full transition-all" style="width: ${progress}%"></div>
            </div>
            ${bodyHtml}
        </div>
    `;
}

export function renderResult(result, partnerName) {
    const pct = result.compatibility_percentage || 0;
    const truthCount = result.truthCount || 0;
    const dareCount = result.dareCount || 0;

    return `
        <div class="game-card game-result-card" style="animation: gameCardAppear 0.4s ease-out">
            <div class="text-center mb-4">
                <p class="text-lg font-bold">🎉 ${t('games.gameComplete')}</p>
            </div>
            <div class="game-result-score text-fuchsia-500" style="animation: compatibilityCountUp 0.8s ease-out">
                <span class="text-4xl font-bold">🎯</span>
                <p class="text-sm mt-1">${result.totalRounds} ${t('games.rounds', { count: result.totalRounds }).replace(/^\d+\s*/, '')}</p>
            </div>
            <div class="flex justify-center gap-6 mt-4">
                <div class="text-center">
                    <span class="text-2xl">🤫</span>
                    <p class="text-sm font-medium">${truthCount} ${t('games.truth')}</p>
                </div>
                <div class="text-center">
                    <span class="text-2xl">🔥</span>
                    <p class="text-sm font-medium">${dareCount} ${t('games.dare')}</p>
                </div>
            </div>
        </div>
    `;
}

export function onGameUpdate(sessionId, data, currentUserId) {
    const card = document.querySelector(`.game-active-card[data-session-id="${sessionId}"]`);
    if (!card) return;
    if (data.status === 'active' && data.state) {
        const partnerName = window._currentChatPartnerName || '';
        const newHtml = renderGameCard(sessionId, data, currentUserId, partnerName);
        if (newHtml) {
            card.outerHTML = newHtml;
        }
    }
}
